"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createLedsService = void 0;
const axios_1 = __importDefault(require("axios"));
const injection_symbols_1 = require("./injection-symbols");
/**
 * Creates the LEDS service composable instance, for use as a plugin in the Vue app.
 */
function createLedsService(options) {
    var _a, _b;
    const { authenticationService } = options;
    let { clientId } = options;
    // Axios instance for LEDS service calls.
    const client = axios_1.default.create({
        baseURL: (_a = options.baseUrl) !== null && _a !== void 0 ? _a : 'https://api.bcbssc.com/',
        timeout: (_b = options.timeout) !== null && _b !== void 0 ? _b : 60000,
        responseType: 'json',
        headers: {
            'Cache-Control': 'no-cache',
            'Content-Type': 'application/json;charset=UTF-8'
        }
    });
    // Intercept request to ensure we are authenticated before making any API call.
    client.interceptors.request.use((config) => __awaiter(this, void 0, void 0, function* () {
        // Re-authenticate if not authenticated
        try {
            if (!(yield authenticationService.isAuthenticated())) {
                yield authenticationService.login();
            }
        }
        catch (error) {
            yield authenticationService.expireSession();
            throw new axios_1.default.Cancel('[leds-service] Session expired. Canceling request. ' + error);
        }
        // Add authorization headers to request
        config.headers = Object.assign(Object.assign(Object.assign({}, config.headers), yield createAuthorizationHeaders()), { 'X-UI-Start-Timestamp': new Date().getTime() });
        return config;
    }), (error) => {
        return Promise.reject(error);
    });
    // Intercept response to attempt to renew session if status shows as expired
    client.interceptors.response.use((response) => {
        logNetworkResponseToSplunk(response);
        return response;
    }, (error) => __awaiter(this, void 0, void 0, function* () {
        const response = error.response;
        if (response) {
            const isAuthenticated = yield authenticationService.isAuthenticated();
            if (isAuthenticated && response.status === 419) {
                yield authenticationService.refreshSession();
            }
            logNetworkResponseToSplunk(response);
        }
        throw error;
    }));
    /**
     * Creates a header map containing the headers required for authorization.
     */
    function createAuthorizationHeaders() {
        return __awaiter(this, void 0, void 0, function* () {
            const headers = {};
            headers['X-IBM-Client-ID'] = `${clientId}`;
            headers['Authorization'] = `Bearer ${yield authenticationService.getAccessToken('leds')}`;
            const { legacyCommwebServiceBaseUrl } = yield authenticationService.getBaseUrls();
            if (legacyCommwebServiceBaseUrl) {
                headers['X-Environment'] = legacyCommwebServiceBaseUrl.replace('https://', '');
            }
            const hostRegion = yield authenticationService.getHostRegion();
            if (hostRegion) {
                headers['X-Region'] = hostRegion;
            }
            return headers;
        });
    }
    /**
     * Update the options used on service creation.
     */
    function useOptions(options) {
        if (options.baseUrl) {
            client.defaults.baseURL = options.baseUrl;
        }
        if (options.clientId) {
            clientId = options.clientId;
        }
        if (options.timeout) {
            client.defaults.timeout = options.timeout;
        }
    }
    /**
     * Determines what the splunk environment/swimlane field should be based on the URL.
     */
    function getSplunkEnvironment() {
        const subdomain = window.location.hostname.replace(/^secure\./, '').split('.')[0];
        const swimlaneSplit = subdomain.split('-');
        let environment = 'production';
        let swimlane = '';
        if (!['www', 'myhealthtoolkit'].includes(subdomain)) {
            if (swimlaneSplit.length > 1) {
                environment = swimlaneSplit[0];
                swimlane = swimlaneSplit[1];
            }
            else if (/^[0-9]$/.test(subdomain.slice(-1))) {
                environment = subdomain.slice(0, -1);
                swimlane = subdomain.slice(-1);
            }
        }
        return { environment, swimlane };
    }
    /**
     * Log the network request details to Splunk for monitoring
     */
    function logNetworkResponseToSplunk(response) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!(yield authenticationService.isAuthenticated())
                    || !response
                    || ((_b = (_a = response.config) === null || _a === void 0 ? void 0 : _a.url) === null || _b === void 0 ? void 0 : _b.includes('analytics/logMessage')))
                    return;
                const elapsedTime = new Date().getTime()
                    - (parseInt(`${(_d = (_c = response.config) === null || _c === void 0 ? void 0 : _c.headers) === null || _d === void 0 ? void 0 : _d['X-UI-Start-Timestamp']}`, 10) || 0);
                let path = ((_j = (_g = ((_f = (_e = response.config) === null || _e === void 0 ? void 0 : _e.headers) === null || _f === void 0 ? void 0 : _f['X-UI-Template-Path'])) !== null && _g !== void 0 ? _g : (_h = response.config) === null || _h === void 0 ? void 0 : _h.url) !== null && _j !== void 0 ? _j : '');
                try {
                    path = decodeURI(new URL(path).pathname);
                }
                catch (error) { /* Allow it to log the non-decoded path */ }
                const { environment, swimlane } = getSplunkEnvironment();
                yield client.post('analytics/logMessage', {
                    event: {
                        message_type: 'network',
                        comm_web_uuid: yield authenticationService.getMonitoringSessionId(),
                        environment,
                        swimlane,
                        platform: 'mhtk3',
                        timestamp: new Date().toISOString(),
                        elapsed_time: elapsedTime,
                        method: ((_l = (_k = response.config) === null || _k === void 0 ? void 0 : _k.method) !== null && _l !== void 0 ? _l : '').toLowerCase(),
                        origin: ((_m = client.defaults.baseURL) !== null && _m !== void 0 ? _m : '').replace(/\/$/, ''),
                        path: '/' + path.replace(/$\//, ''),
                        status: response.status,
                        gtid: (_o = response.headers) === null || _o === void 0 ? void 0 : _o['x-global-transaction-id'],
                    },
                    memberToken: yield authenticationService.getAccessToken('leds')
                });
            }
            catch (error) {
                // Ignore all errors - monitoring is not mission critical
            }
        });
    }
    // Build the plugin object.
    const ledsService = {
        client,
        createAuthorizationHeaders,
        useOptions,
        install(app) {
            app.provide(injection_symbols_1.ledsServiceKey, ledsService);
            app.provide(injection_symbols_1.ledsServiceClientKey, client);
        }
    };
    return ledsService;
}
exports.createLedsService = createLedsService;
//# sourceMappingURL=leds-service.js.map